import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import QRCode from 'qrcode';
import { Shield, Users, Activity, CheckCircle, XCircle, AlertCircle, Clock, Search, Download, RefreshCw, MoreVertical, QrCode, X } from 'lucide-react';

const DAWN_USER_ID = '2b26d577-5720-4cb4-bcca-dc03875b3264';

interface TwoFAStats {
  user_type: string;
  total_users: number;
  twofa_enabled_count: number;
  twofa_adoption_percentage: number;
  active_twofa_users: number;
  inactive_twofa_users: number;
  avg_days_to_enable: number;
  last_twofa_enabled_at: string;
  last_verification_at: string;
}

interface UserDetail {
  user_id: string;
  user_type: string;
  email: string;
  full_name: string;
  is_2fa_enabled: boolean;
  twofa_enabled_at: string;
  last_2fa_verified_at: string;
  account_created_at: string;
  status: 'disabled' | 'enabled_never_used' | 'active' | 'moderate' | 'inactive';
  days_since_last_verification: number;
}

interface RecentActivity {
  user_id: string;
  user_type: string;
  email: string;
  full_name: string;
  activity_timestamp: string;
  activity_type: 'enabled' | 'verified';
}

export default function TwoFactorManagement() {
  const navigate = useNavigate();
  const { isAdmin, loading } = useAuth();
  const [stats, setStats] = useState<TwoFAStats[]>([]);
  const [userDetails, setUserDetails] = useState<UserDetail[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuUserId, setOpenMenuUserId] = useState<string | null>(null);
  const [qrModal, setQrModal] = useState<{ open: boolean; dataUrl: string; loading: boolean; error: string | null }>({
    open: false,
    dataUrl: '',
    loading: false,
    error: null,
  });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, loading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [statsResult, detailsResult, activityResult] = await Promise.all([
        supabase.from('twofa_statistics').select('*'),
        supabase.from('twofa_user_details').select('*'),
        supabase.from('twofa_recent_activity').select('*').limit(50)
      ]);

      if (statsResult.error) throw statsResult.error;
      if (detailsResult.error) throw detailsResult.error;
      if (activityResult.error) throw activityResult.error;

      setStats(statsResult.data || []);
      setUserDetails(detailsResult.data || []);
      setRecentActivity(activityResult.data || []);
    } catch (err) {
      console.error('Error fetching 2FA data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load 2FA data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuUserId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleViewQR = async (userId: string) => {
    setOpenMenuUserId(null);
    setQrModal({ open: true, dataUrl: '', loading: true, error: null });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-generate-2fa-qr`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
            Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ target_user_id: userId }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate QR code');
      const dataUrl = await QRCode.toDataURL(data.otpauth_url, { width: 280, margin: 2 });
      setQrModal({ open: true, dataUrl, loading: false, error: null });
    } catch (err) {
      setQrModal({ open: true, dataUrl: '', loading: false, error: err instanceof Error ? err.message : 'Failed to load QR code' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-orange-100 text-orange-800';
      case 'enabled_never_used':
        return 'bg-blue-100 text-blue-800';
      case 'disabled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active (< 7 days)';
      case 'moderate':
        return 'Moderate (< 30 days)';
      case 'inactive':
        return 'Inactive (> 30 days)';
      case 'enabled_never_used':
        return 'Never Used';
      case 'disabled':
        return 'Disabled';
      default:
        return status;
    }
  };

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case 'consultant':
        return 'Consultant';
      case 'workday_client':
        return 'Workday Client';
      case 'admin':
        return 'Admin';
      default:
        return type;
    }
  };

  const filteredUsers = userDetails.filter(user => {
    const matchesUserType = selectedUserType === 'all' || user.user_type === selectedUserType;
    const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus;
    const matchesSearch = searchQuery === '' ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.full_name.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesUserType && matchesStatus && matchesSearch;
  });

  const exportToCSV = () => {
    const headers = ['Email', 'Name', 'User Type', 'Status', '2FA Enabled', 'Enabled At', 'Last Verified'];
    const rows = filteredUsers.map(user => [
      user.email,
      user.full_name,
      getUserTypeLabel(user.user_type),
      getStatusLabel(user.status),
      user.is_2fa_enabled ? 'Yes' : 'No',
      user.twofa_enabled_at ? new Date(user.twofa_enabled_at).toLocaleDateString() : 'N/A',
      user.last_2fa_verified_at ? new Date(user.last_2fa_verified_at).toLocaleDateString() : 'N/A'
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `2fa-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading || isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  const totalStats = stats.reduce((acc, stat) => ({
    total_users: acc.total_users + stat.total_users,
    twofa_enabled_count: acc.twofa_enabled_count + stat.twofa_enabled_count,
    active_twofa_users: acc.active_twofa_users + stat.active_twofa_users,
    inactive_twofa_users: acc.inactive_twofa_users + stat.inactive_twofa_users
  }), { total_users: 0, twofa_enabled_count: 0, active_twofa_users: 0, inactive_twofa_users: 0 });

  const overallAdoptionRate = totalStats.total_users > 0
    ? ((totalStats.twofa_enabled_count / totalStats.total_users) * 100).toFixed(1)
    : '0';

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Two-Factor Authentication</h1>
            <p className="text-gray-500 mt-2">Monitor and manage 2FA adoption across all user types</p>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{totalStats.total_users}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">2FA Enabled</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{totalStats.twofa_enabled_count}</p>
                <p className="text-sm text-green-600 mt-1">{overallAdoptionRate}% adoption</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{totalStats.active_twofa_users}</p>
                <p className="text-sm text-gray-600 mt-1">Last 7 days</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Activity className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Inactive</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{totalStats.inactive_twofa_users}</p>
                <p className="text-sm text-orange-600 mt-1">Need attention</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {stats.map(stat => (
            <div key={stat.user_type} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{getUserTypeLabel(stat.user_type)}</h3>
                <Shield className="w-5 h-5 text-gray-400" />
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Users</span>
                  <span className="font-semibold text-gray-900">{stat.total_users}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">2FA Enabled</span>
                  <span className="font-semibold text-green-600">{stat.twofa_enabled_count}</span>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${stat.twofa_adoption_percentage}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 text-center">{stat.twofa_adoption_percentage}% adoption rate</p>

                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-600">Active (7d)</span>
                    <span className="text-sm font-medium text-green-600">{stat.active_twofa_users}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Inactive (30d+)</span>
                    <span className="text-sm font-medium text-orange-600">{stat.inactive_twofa_users}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <h2 className="text-xl font-semibold text-gray-900">User Details</h2>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <select
                  value={selectedUserType}
                  onChange={(e) => setSelectedUserType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="consultant">Consultants</option>
                  <option value="workday_client">Workday Clients</option>
                  <option value="admin">Admins</option>
                </select>

                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="moderate">Moderate</option>
                  <option value="inactive">Inactive</option>
                  <option value="enabled_never_used">Never Used</option>
                  <option value="disabled">Disabled</option>
                </select>

                <button
                  onClick={exportToCSV}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">2FA Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enabled At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Verified</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map(user => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getUserTypeLabel(user.user_type)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.status)}`}>
                        {getStatusLabel(user.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.is_2fa_enabled ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                            <span className="text-sm text-green-700">Enabled</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-500">Disabled</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.twofa_enabled_at ? new Date(user.twofa_enabled_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_2fa_verified_at ? (
                        <div>
                          <div>{new Date(user.last_2fa_verified_at).toLocaleDateString()}</div>
                          {user.days_since_last_verification !== null && (
                            <div className="text-xs text-gray-400">
                              {Math.floor(user.days_since_last_verification)} days ago
                            </div>
                          )}
                        </div>
                      ) : (
                        'Never'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.user_id === DAWN_USER_ID && (
                        <div className="relative" ref={openMenuUserId === user.user_id ? menuRef : null}>
                          <button
                            onClick={() => setOpenMenuUserId(openMenuUserId === user.user_id ? null : user.user_id)}
                            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openMenuUserId === user.user_id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                              <button
                                onClick={() => handleViewQR(user.user_id)}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <QrCode className="w-4 h-4 text-gray-500" />
                                View Google Auth QR
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No users found matching the filters</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
            <p className="text-sm text-gray-600 mt-1">Last 50 2FA events</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentActivity.map((activity, index) => (
                  <tr key={`${activity.user_id}-${activity.activity_type}-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{activity.full_name}</div>
                        <div className="text-sm text-gray-500">{activity.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getUserTypeLabel(activity.user_type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {activity.activity_type === 'enabled' ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          2FA Enabled
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Verified Login
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(activity.activity_timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {recentActivity.length === 0 && (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {qrModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-gray-700" />
                <h3 className="font-semibold text-gray-900">Google Authenticator QR Code</h3>
              </div>
              <button
                onClick={() => setQrModal({ open: false, dataUrl: '', loading: false, error: null })}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 flex flex-col items-center">
              {qrModal.loading && (
                <div className="py-10 flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                  <p className="text-sm text-gray-500">Generating QR code...</p>
                </div>
              )}
              {qrModal.error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg w-full">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{qrModal.error}</p>
                </div>
              )}
              {qrModal.dataUrl && (
                <>
                  <p className="text-sm text-gray-600 text-center mb-4">
                    Scan this QR code with Google Authenticator to set up 2FA for Dawn Gallman Mobley.
                  </p>
                  <div className="border border-gray-200 rounded-lg p-2 bg-white">
                    <img src={qrModal.dataUrl} alt="2FA QR Code" className="w-64 h-64" />
                  </div>
                  <p className="text-xs text-gray-400 mt-4 text-center">
                    This QR code is only visible to admins. Keep it confidential.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
